import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ChevronRight, Loader2, Heart, Monitor } from "lucide-react";
import { CALENDLY_URL } from "@/lib/calendly";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSection() {
  const [step, setStep] = useState("form"); // "form" | "calendar"
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [newsletter, setNewsletter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const calendarRef = useRef(null);

  // Mount Calendly inline widget once we've collected name+email
  useEffect(() => {
    if (step !== "calendar") return;
    if (!calendarRef.current) return;

    const mount = () => {
      if (!window.Calendly || typeof window.Calendly.initInlineWidget !== "function") {
        return setTimeout(mount, 300);
      }
      calendarRef.current.innerHTML = "";
      window.Calendly.initInlineWidget({
        url: CALENDLY_URL,
        parentElement: calendarRef.current,
        prefill: {
          firstName,
          email,
          name: firstName,
        },
      });
    };
    mount();
  }, [step, firstName, email]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const name = firstName.trim();
    const mail = email.trim();
    if (!name || !mail) {
      setError("Fyll i både förnamn och e-post.");
      return;
    }

    setLoading(true);

    // 1) GTM Lead event — fire BEFORE we switch view so the tag has a stable DOM
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "formular_lead",
        first_name: name,
        has_newsletter_optin: newsletter,
      });
    } catch (_) {
      /* ignore */
    }

    // 2) Resend newsletter — only if the checkbox is ticked
    if (newsletter) {
      try {
        await axios.post(`${API}/newsletter`, {
          first_name: name,
          email: mail,
        });
      } catch (err) {
        // Don't block the booking flow if newsletter fails
        console.warn("Newsletter signup failed:", err?.response?.data || err?.message);
      }
    }

    // 3) Reveal the Calendly inline widget prefilled
    setLoading(false);
    setStep("calendar");

    // Scroll a bit so user sees the calendar
    setTimeout(() => {
      calendarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  return (
    <section id="boka" className="py-24 md:py-32 bg-[#F3EFEA]/60">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <p className="text-xs tracking-[0.3em] uppercase text-[#8C7362] mb-6">
          Första steget
        </p>
        <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#2C2825] leading-[1.05]">
          Ta det första steget mot ett lugnare inre.
        </h2>
        <p className="mt-6 text-[#5C5651] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
          Börja med att boka ett kostnadsfritt samtal med mig, så kan vi
          tillsammans se vad som passar just dig. Samtalen är för kvinnor.
        </p>

        {step === "form" ? (
          <form
            onSubmit={onSubmit}
            data-testid="booking-form"
            className="mt-12 bg-white border border-[#E6DFD7] rounded-3xl p-8 md:p-10 text-left shadow-sm"
            noValidate
          >
            <div className="space-y-5">
              <Field label="Förnamn *">
                <Input
                  data-testid="booking-first-name"
                  type="text"
                  placeholder="Ditt förnamn"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  required
                  className="h-12 rounded-full bg-[#FAF9F6] border-[#DCD5CE] px-5 focus-visible:ring-[#A98C78] focus-visible:ring-offset-0"
                />
              </Field>

              <Field label="E-post *">
                <Input
                  data-testid="booking-email"
                  type="email"
                  placeholder="Din e-postadress"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  className="h-12 rounded-full bg-[#FAF9F6] border-[#DCD5CE] px-5 focus-visible:ring-[#A98C78] focus-visible:ring-offset-0"
                />
              </Field>

              <label className="flex items-start gap-3 pt-1 cursor-pointer select-none">
                <Checkbox
                  data-testid="booking-newsletter"
                  checked={newsletter}
                  onCheckedChange={(v) => setNewsletter(!!v)}
                  className="mt-1 border-[#C9BFB5] data-[state=checked]:bg-[#A98C78] data-[state=checked]:border-[#A98C78]"
                />
                <span className="text-sm text-[#5C5651] leading-relaxed">
                  Ja, jag vill få Aminahs nyhetsbrev
                </span>
              </label>

              {error && (
                <p className="text-sm text-red-700" data-testid="booking-error">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="booking-submit"
                className="w-full sm:w-auto h-14 rounded-full bg-[#8C7362] hover:bg-[#6B5547] text-white px-10 text-sm font-bold tracking-[0.18em] uppercase transition-colors shadow-sm disabled:opacity-70"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Skickar...
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    Fortsätt till kalendern
                    <ChevronRight className="h-4 w-4" />
                  </span>
                )}
              </Button>

              <p className="text-[11px] text-[#8C7362] leading-relaxed">
                Vi behandlar dina uppgifter varsamt. Du kan avregistrera dig
                när du vill.
              </p>
            </div>
          </form>
        ) : (
          <div className="mt-10">
            <p className="text-sm text-[#5C5651] mb-4">
              Välj en tid som passar dig, {firstName}. Dina uppgifter är redan ifyllda.
            </p>
            <div
              ref={calendarRef}
              data-testid="calendly-inline"
              className="w-full rounded-3xl bg-white border border-[#E6DFD7] shadow-sm overflow-visible calendly-inline-wrap"
            />
          </div>
        )}

        <p className="mt-6 text-sm text-[#8C7362] flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1.5"><Heart className="h-3.5 w-3.5" /> 100 % kostnadsfritt</span>
          <span className="text-[#C9BFB5]">·</span>
          <span className="inline-flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5" /> Samtalet sker online via dator eller telefon</span>
        </p>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs tracking-[0.2em] uppercase text-[#8C7362]">
        {label}
      </span>
      {children}
    </label>
  );
}
